import { jsx as _jsx } from "react/jsx-runtime";
// Career Miniapp — TrackingContext (Unified plan M-2)
// Apply-flow tracking paraméterek: URL → sessionStorage → __CAREER_CONFIG__.tracking_initial 3-szintű fallback
// Egyszer-és-mindenkorra rögzítjük a session induláskori állapotot — innentől perzisztens.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { adClickIdsAllowed } from '../utils/consent';
const SS_KEY = 'career_tracking_v1';
const UTM_KEYS = [
    'utm', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid',
];
function readQueryParams() {
    if (typeof window === 'undefined')
        return {};
    const out = {};
    try {
        const sp = new URLSearchParams(window.location.search);
        UTM_KEYS.forEach(k => {
            const v = sp.get(k);
            if (v)
                out[k] = v;
        });
        const ref = sp.get('referral_id');
        if (ref)
            out.referral_id = ref;
        const getRef = sp.get('referralId') || sp.get('get_referralId');
        if (getRef)
            out.get_referralId = getRef;
        const src = sp.get('source');
        if (src)
            out.source = src;
        // hrfy.hu rövidlinkes érkezés jelzője — a track-view ebből tudja, hogy a
        // megtekintés a rövidlink-domainre könyvelendő (SSR Singlejob paritás)
        const hrfy = sp.get('hrfy');
        if (hrfy)
            out.hrfy = hrfy;
    }
    catch (_e) { /* ignore */ }
    return out;
}
function readSessionStorage() {
    if (typeof window === 'undefined')
        return {};
    try {
        const raw = window.sessionStorage.getItem(SS_KEY);
        if (!raw)
            return {};
        const obj = JSON.parse(raw);
        return (obj && typeof obj === 'object') ? obj : {};
    }
    catch (_e) {
        return {};
    }
}
function readSsrInitial() {
    if (typeof window === 'undefined')
        return {};
    const cfg = window.__CAREER_CONFIG__;
    return cfg && cfg.tracking_initial && typeof cfg.tracking_initial === 'object' ? cfg.tracking_initial : {};
}
function mergeNonEmpty(...sources) {
    const out = {};
    for (const s of sources) {
        if (!s)
            continue;
        for (const [k, v] of Object.entries(s)) {
            if (v !== undefined && v !== null && v !== '') {
                out[k] = v;
            }
        }
    }
    return out;
}
function persistSession(params) {
    if (typeof window === 'undefined')
        return;
    try {
        // [F-COOKIECMP] Hirdetési kattintás-azonosítók (gclid/fbclid) targeting-
        // hozzájárulás nélkül NEM perzisztálhatók a sessionStorage-be — memóriában
        // (a context értékében) megmaradnak az aktuális oldalbetöltés idejére, így
        // ha a látogató a banneren elfogad, az apply még ugyanazon a betöltésen
        // hozzáfér. Legacy oldalon (nincs banner) a viselkedés változatlan.
        const toPersist = { ...params };
        if (!adClickIdsAllowed()) {
            delete toPersist.gclid;
            delete toPersist.fbclid;
        }
        if (Object.keys(toPersist).length === 0)
            return;
        window.sessionStorage.setItem(SS_KEY, JSON.stringify(toPersist));
    }
    catch (_e) { /* ignore quota */ }
}
const TrackingCtx = createContext(null);
export function TrackingProvider({ children }) {
    // FONTOS: useState initializer egyszer fut — innentől stabil object reference.
    // Így a useJob(slug, referral_id) NEM triggerel két fetch-et a mount körül.
    const [tracking] = useState(() => {
        // Prioritás: URL > sessionStorage > SSR initial (utm_data cookie alapján)
        return mergeNonEmpty(readSsrInitial(), readSessionStorage(), readQueryParams());
    });
    // Csak persist-elünk a sessionStorage-be, NEM setTracking
    useEffect(() => {
        persistSession(tracking);
    }, [tracking]);
    const value = useMemo(() => ({
        tracking,
        getCurrent: () => tracking,
    }), [tracking]);
    return _jsx(TrackingCtx.Provider, { value: value, children: children });
}
export function useTrackingParams() {
    const ctx = useContext(TrackingCtx);
    if (!ctx) {
        // Fallback ha nincs provider (pl. unit testben) — üres tracking
        return {};
    }
    return ctx.tracking;
}
